import { Form, Table } from "antd";
import React, { useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { PaginationComponent } from "../../../Common/Pagination/PaginationComponent";
import PermissionCheck from "../../../Common/PermissionCheck";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useDeleteModal } from "../../../hooks/useDeleteModal";
import { useHandlePagination } from "../../../hooks/useHandlePagination";
import { useHttp } from "../../../hooks/useHttp";
import { EditableCell } from "../../SeasonOfficials/EditableCell";
import { getRoleTranslationKey } from "~utilities/getRoleTranslationKey";
import { getColumns } from "./columnsRepository";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import { useNotification } from "~hooks/useNotification";
import { findImageByTypes } from "~utilities/findImageByTypes.ts";
import { ImageTypes } from "~enums/imageTypes.ts";

const PermissionName = PermissionNames.ROLES;

function StaffMembers(props) {
  const { roles, pageRef, refetch, loading } = props;

  const { formatMessage } = useIntl();

  const { sport, organizationId, entityId, customerId, seasonId } = useParams();
  const { seasonStaffRoles } = useSportConfiguration();
  const staffMembers = roles?.data || [];

  const deleteModal = useDeleteModal({
    headingText: formatMessage({ id: "delete" }),
    refreshTrigger: refetch,
    pageRef: props.pageRef,
  });
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState(null);
  const { loading: saveInProgress, request } = useHttp();
  const notification = useNotification();

  const onEdit = (record) => {
    form.setFieldsValue({
      ...record,
    });
    setEditingKey(record.roleId);
  };
  const onCancel = () => {
    setEditingKey(null);
  };

  const onSave = (record) => {
    request({
      url: `/v1/${sport}/o/${organizationId}/roles/${record.roleId}`,
      method: "PUT",
      body: {
        ...form.getFieldsValue(),
        entityId: record.entityId,
      },
    })
      .then((response) => {
        notification.success({
          message: formatMessage({
            id: "staff.update.success",
            defaultMessage: "Staff updated successfully",
          }),
          placement: "bottomRight",
        });
        onCancel();
        refetch();
      })
      .catch((reason) => {
        console.error("E", reason);
        notification.error({
          message: formatMessage({
            id: "staff.update.failed",
            defaultMessage: "Staff update failed",
          }),
          placement: "bottomRight",
        });
      });
  };

  const handleDeleteIntent = (roleId) => {
    const URL = `/v1/${sport}/o/${organizationId}/roles/${roleId}`;
    deleteModal.setData.setUrl(URL);
    deleteModal.setData.setTextContent(formatMessage({ id: "delete.item.confirm" }));
    deleteModal.visibility.open();
  };

  const { pageSize, currentPage, handlePaginationChange } = useHandlePagination();

  const staffData = staffMembers.map((x) => {
    const person = roles?.includes?.resources?.persons?.[x.person.id];
    return {
      roleId: x.roleId,
      role: x.role,
      person: {
        id: person?.personId,
        nameFullLocal: person?.nameFullLocal,
        image: findImageByTypes(person?.images, [ImageTypes.PersonHead])?.url
      },
      entityId: x.entityId,
    };
  });

  const columns = getColumns({
    formatMessage,
    handleDeleteIntent,
    onEdit,
    onCancel,
    editingKey,
    onSave,
    saveInProgress,
    customerId,
    sport,
    organizationId,
  });
  const isEditing = (record) => editingKey === record.roleId;

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }

    const options = {
      role: seasonStaffRoles.map((x) => ({
        value: x,
        label: formatMessage({
          id: `roles.${getRoleTranslationKey(x, sport)}`,
        }),
      })),
    };

    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: options[col.dataIndex] ? "select" : "text",
        options: options[col.dataIndex],
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  return (
    <>
      <Form form={form} component={false}>
        <Table
          size="small"
          columns={mergedColumns}
          dataSource={staffData}
          components={{
            body: {
              cell: EditableCell,
            },
          }}
          loading={loading}
          scroll={{ x: true }}
          pagination={{
            current: currentPage,
            defaultPageSize: pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            position: ["bottomCenter"],
            total: staffData?.length || 0,
            onChange: handlePaginationChange,
            showTotal: (total, range) => <PaginationComponent pageSize={pageSize} total={total} range={range}/>,
          }}
        />
      </Form>
      <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.DELETE]}>
        {deleteModal.element()}
      </PermissionCheck>
    </>
  );
}

export default StaffMembers;
